import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, ContextualField, Dictionary, Group, GroupConfigMode, GuidObjectType, IApplicationConfiguration, IBindable, IFrequency, IValueViewModel, Miniature, PropertyType, Template, TimeInterval, TimeUnit, ValidationPageConfiguration, ValidationScope } from '../../../models';
import { CustomProperties, Documentation, LongComment, Name, ShortComment } from '../../../common/helpers/contextual/predefinedContextualFields';
import { createGroupMiniatures } from './createGroupMiniatures';
import { referenceGroupField } from './referenceGroupField';
import { groupForbiddenCharacters, groupForbiddenStartingCharacters, guidEmpty } from '../../../common/constants';
import { IIntervalValidationService } from '../../../services/helpers';
import { IBindingCheckerService, IBindingResult, ILoggerService, IStoreService } from '../../../services';
import { BindingIssuesModalConfig } from '../../../components/bindingIssuesModal/bindingIssuesModalConfig';
import { IWidget } from '../../../widgets';
import { Store, StoreElement } from '../../../stateManagement';
import { IRootScope } from '../../../IRootScope';
import { groupTreeChanged } from '../../../common';

class ModernModelContextualController implements angular.IComponentController {
    formController: angular.IFormController;
    isSaving: boolean;
    miniatures: Miniature[] = [];
    modalConfig: BindingIssuesModalConfig;
    properties: ContextualField[];
    selectedItem: Group & { referenceGroup?: string };
    widgetsDictionary: Dictionary<IWidget[]>;

    readonly appConfig: IApplicationConfiguration;
    readonly groups: Group[];
    readonly onCancel: () => void;
    readonly onSave: (value: { group: Group; referenceGroup?: string }) => angular.IPromise<Object>;
    readonly referenceGroupDictionary: { [type: string]: string };
    readonly templates: Template[];

    private _validationPageMapping: { [name: string]: string };
    private _defaultIntervalInSeconds: number;

    static $inject = [
        '$q',
        'translatedLoggerService',
        'intervalValidationService',
        'storeService',
        'bindingCheckerService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _loggerService: ILoggerService,
        private _intervalValidationService: IIntervalValidationService,
        private _storeService: IStoreService,
        private _bindingCheckerService: IBindingCheckerService
    ) {
    }

    $onInit() {
        this._storeService.register(
            store => this.refreshStore(store),
            [StoreElement.ValidationPageConfigurations]
        );
        this._defaultIntervalInSeconds = this.defaultInterval(this.appConfig);
        const nameField = Object.assign({}, Name);
        const oldIsValid = nameField.isValid || (() => ({}));
        const isValid = (value => {
            const newValidityConditions = {
                'forbidden': !value || this.isNotForbiddenName(value),
                'duplicate': !value || this.isNotDuplicateName(value)
            };

            const oldValidityConditions = oldIsValid(value);
            return Object.assign(oldValidityConditions, newValidityConditions);
        });
        nameField.isValid = isValid;
        const userFields = Object.assign({}, CustomProperties);
        userFields.extraData.type = GuidObjectType.Group;
        const referenceGroup = Object.assign({}, referenceGroupField);
        this.updateReferenceGroups(referenceGroup);
        this.properties = [
            nameField,
            Object.assign({}, ShortComment),
            Object.assign({}, LongComment),
            userFields,
            {
                label: 'Type',
                modelName: 'type',
                propertyName: 'typeProperty',
                propertyType: PropertyType.Text,
                required: false,
                icon: ['fa-sitemap', 'fa-neuter'],
                onChange: viewValue => {
                    const configMode = this.selectedItem.configMode;
                    const type = viewValue;
                    this.validateReferenceGroup(referenceGroup, configMode, type);
                    this.updateProperties('typeProperty', type);
                }
            }, {
                label: 'Template',
                modelName: 'templateGuid',
                propertyName: 'templateGuidProperty',
                propertyType: PropertyType.Select,
                required: false,
                icon: ['fa-clipboard'],
                extraData: {
                    options: [{ key: guidEmpty, value: ' ' }]
                        .concat((this.templates || []).map(d => {
                            return {
                                key: d.guid,
                                value: d.name
                            };
                        }))
                }
            }, {
                modelName: 'frequency',
                propertyType: PropertyType.Frequency,
                label: 'Frequency',
                placeholder: 'Frequency',
                propertyName: 'frequencyProperty',
                errorName: 'frequencyPropertyInterval',
                required: false,
                icon: 'fa-clock-o',
                extraData: {
                    frequencyValidationRules: [
                        {
                            key: 'format',
                            isValid: freq => {
                                const expr = new RegExp('^[0-9]*$');
                                const isValid = expr.test(freq?.interval?.toString() ?? '');
                                return isValid;
                            }
                        },
                        {
                            key: 'value',
                            isValid: freq => {
                                const value = freq.intervalTimeUnit;
                                const newInterval = new TimeInterval();
                                newInterval.interval = freq.interval;
                                newInterval.unit = (value || value === 0)
                                    ? value
                                    : this.selectedItem.frequency.intervalTimeUnit;
                                const duration = newInterval.duration();
                                const isValid = duration.asSeconds() % this._defaultIntervalInSeconds === 0;
                                return isValid;
                            }
                        }, {
                            key: 'strategy',
                            isValid: freq => {
                                const guid = this.selectedItem.approvalStrategyGuid;
                                return this.isStrategyValid(guid, freq);
                            }
                        },
                        {
                            key: 'interval',
                            isValid: freq => {
                                return this._intervalValidationService.isValid(freq);
                            }
                        }
                    ]
                },
                messages: [
                    { key: 'min', value: 'Invalid interval format. Interval must be an integer greater than 0.' },
                    { key: 'format', value: 'Invalid interval format. Interval must be an integer greater than 0.' },
                    { key: 'value', value: 'Invalid interval. Interval must be a multiple of the database frequency.' },
                    { key: 'strategy', value: 'Approval can only be set for daily or monthly frequency' },
                    { key: 'interval', value: 'Unsupported time interval. Refer to section 7.2 of WiDE documentation for supported intervals.' },
                ],
                helpReference: '_Processing/Processing_groups/#group-frequency'
            }, {
                modelName: 'approvalStrategyGuid',
                propertyType: PropertyType.ApprovalStrategyGuid,
                label: 'Approval strategy',
                placeholder: 'Approval strategy',
                propertyName: 'approvalStrategyGuidProperty',
                required: false,
                icon: 'fa-thumbs-o-up',
                hide: !this.appConfig?.validationEnabled,
                isValid: (value: string) => {
                    const isStrategyValid = this.isStrategyValid(value, this.selectedItem.frequency);
                    return { 'strategy': isStrategyValid };
                },
                messages: [
                    { key: 'strategy', value: 'Approval can only be set for daily or monthly frequency' },
                ],
                extraData: {
                }
            }, {
                modelName: 'configMode',
                propertyType: PropertyType.ValidationConfigMode,
                label: 'Validation',
                placeholder: 'Validation',
                propertyName: 'configModeProperty',
                required: false,
                disabled: !this.selectedItem?.type,
                icon: 'fa-check-square-o',
                onChange: (viewValue, value: number) => {
                    const type = this.selectedItem.type;
                    const configMode = value;
                    this.validateReferenceGroup(referenceGroup, configMode, type);
                },
                extraData: {
                }
            }, {
                modelName: 'validationScope',
                propertyType: PropertyType.Select,
                label: 'Validation scope',
                placeholder: 'Validation scope',
                propertyName: 'validationScopeProperty',
                required: true,
                icon: 'fa-binoculars',
                extraData: {
                    options: [
                        { key: ValidationScope.SinglePeriod, value: 'Single period' },
                        { key: ValidationScope.MultiplePeriod, value: 'Multiple period' },
                    ],
                    defaultValue: ValidationScope.SinglePeriod
                }
            },
            referenceGroup,
            {
                modelName: 'isHomePage',
                propertyType: PropertyType.Boolean,
                label: 'Home page',
                placeholder: 'Home page',
                propertyName: 'isHomePage',
                icon: 'fa-home',
            },
            Object.assign({}, Documentation)
        ];
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['templates']) {
            this.initMiniatures();
            this.updateReferenceGroups();
            if (this.properties && this.templates) {
                const prop = this.properties.filter(f => f.modelName === 'templateGuid')[0];
                prop.extraData = {
                    options: [{ key: guidEmpty, value: ' ' }]
                        .concat(this.templates.map(d => {
                            return {
                                key: d.guid,
                                value: d.name
                            };
                        }))
                };
            }
        }
        if (changesObj['groups']) {
            this.initMiniatures();
            this.updateReferenceGroups();
            this.updateProperties();
        }
        if (changesObj['selectedItem'] && this.selectedItem && this.properties) {
            this.updateReferenceGroups();
            this.updateProperties();
        }
        if (changesObj['appConfig'] && this.appConfig) {
            if (this.properties) {
                const prop = this.properties
                    .filter(f => f.modelName === 'approvalStrategyGuid')[0];
                prop.hide = !this.appConfig?.validationEnabled;
            }
            this._defaultIntervalInSeconds = this.defaultInterval(this.appConfig);
        }
    }

    save() {
        return this.saveGroup(this.selectedItem, this.selectedItem.referenceGroup)
            .then(() => {
                if (this.onSave) {
                    return this.onSave({ group: this.selectedItem, referenceGroup: this.selectedItem.referenceGroup });
                }
            });
    }

    cancel() {
        this.updateReferenceGroups();
        return this.onCancel
            ? this.onCancel()
            : this._$q.when();
    }

    modalSeriesMapConfirm() {
        this._bindingCheckerService.remapBoundSeries(this.modalConfig.seriesByWidget, this.modalConfig.bindResult);
        this.actualSave(this.selectedItem);
    }

    private refreshStore(store: Store) {
        const widgetsDictionary: Dictionary<IWidget[]> = {};
        const validationPageMapping = {};

        _.values<ValidationPageConfiguration>(store.validationPageConfigurations.dictionary)
            .forEach(d => {
                widgetsDictionary[d.name] = d.validationWidgets.map(w => w);
                validationPageMapping[d.name] = d.referenceGroup;
            });

        this.widgetsDictionary = widgetsDictionary;
        this._validationPageMapping = validationPageMapping;
    }

    private updateProperties(property?: string, value?: string) {
        if (this.properties) {
            const configModeProperty = this.properties.find(property => property.propertyName === 'configModeProperty');
            const referenceGroupProperty = this.properties.find(property => property.propertyName === 'referenceGroupProperty');
            let isValidationByTypeDisabled = !this.selectedItem?.type;
            // #38589: If type is emptied, set validation by group and empty reference group.
            if (property === 'typeProperty') {
                isValidationByTypeDisabled = (value === '');
                if (isValidationByTypeDisabled) {
                    this.selectedItem.configMode = GroupConfigMode.ByGroup;
                    this.selectedItem.referenceGroup = undefined;
                }
                referenceGroupProperty.hide = this.selectedItem.configMode === GroupConfigMode.ByGroup;
            }
            // #38589: Avoid selection of validation by type and reference group if type is empty.
            configModeProperty.disabled = isValidationByTypeDisabled;
            referenceGroupProperty.disabled = isValidationByTypeDisabled;
        }
    }

    private defaultInterval(appConfig: IApplicationConfiguration): number {
        if (appConfig) {
            const interval = new TimeInterval();
            interval.interval = appConfig.interval;
            interval.unit = appConfig.intervalTimeUnit;
            const duration = interval.duration();
            const defaultIntervalInSeconds = duration.asSeconds();
            return defaultIntervalInSeconds;
        }
        else {
            return null;
        }
    }

    private isStrategyValid(guid: string, freq?: IFrequency) {
        const tu = TimeUnit;
        let isValid = true;

        if (guid && guid !== guidEmpty) {
            if (!freq) {
                freq = {} as IFrequency;
                freq.interval = this.selectedItem.frequency.interval;
                freq.intervalTimeUnit = this.selectedItem.frequency.intervalTimeUnit;
            }

            // fall back on application interval
            if (freq.interval === 0) {
                freq.interval = this.appConfig.interval;
                freq.intervalTimeUnit = this.appConfig.intervalTimeUnit;
            }

            isValid = freq.interval === 1
                && (freq.intervalTimeUnit === tu.Day || freq.intervalTimeUnit === tu.Month);
        }
        return isValid;
    }

    private validateReferenceGroup(referenceGroupField: ContextualField, configMode: GroupConfigMode, type: string) {
        referenceGroupField.hide = configMode === GroupConfigMode.ByGroup;
        let groups: Group[] = [];
        if (this.groups) {
            groups = groups.concat(this.groups.filter(g => g.type === type));
        }
        if (!groups.filter(g => g.guid === this.selectedItem.guid).length) {
            groups = groups.concat([this.selectedItem]);
        }
        referenceGroupField.extraData.groups = groups;
        let rg = this.referenceGroupDictionary && this.referenceGroupDictionary[type]
            ? this.referenceGroupDictionary[type]
            : null;
        if (rg && groups.map(g => g.guid).indexOf(rg) < 0) {
            rg = null;
        }
        this.selectedItem.referenceGroup = rg;
    }

    private updateReferenceGroups(refGroupField?: ContextualField) {
        if (!refGroupField && this.properties) {
            refGroupField = this.properties.filter(f => f.modelName === 'referenceGroup')[0];
        }
        if (refGroupField && this.selectedItem) {
            this.validateReferenceGroup(refGroupField, this.selectedItem.configMode, this.selectedItem.type);
        }
    }

    private initMiniatures() {
        if (this.groups && this.templates) {
            this.miniatures = createGroupMiniatures(this.templates, this.groups);
        }
    }

    private isNotForbiddenName(value: string): boolean {
        const newName = value.toLowerCase();
        const isValid = !_.some(newName, c => groupForbiddenCharacters.indexOf(String(c)) >= 0) &&
            !_.some(groupForbiddenStartingCharacters, c => _.startsWith(newName, c)) &&
            !(newName === 'root');
        return isValid;
    }

    private isNotDuplicateName(value: string): boolean {
        const newName = value.toLowerCase();
        const isValid = !_.some(this.groups, t => {
            return t.parentGUID === this.selectedItem.parentGUID
                && t.name?.toLowerCase() === newName
                && t.guid !== this.selectedItem.guid;
        });
        return isValid;
    }


    private saveGroup(group: Group, referenceGroupGuid?: string): angular.IPromise<void> {
        const current = this._validationPageMapping[group.type];
        if (referenceGroupGuid && current !== referenceGroupGuid) {
            return this.checkBindings(group, current, referenceGroupGuid);
        } else {
            const group: Group & { referenceGroup?: string } = Object.assign(this.selectedItem, { referenceGroup: referenceGroupGuid });
            return this.actualSave(group);
        }
    }

    private actualSave(group: Group & { referenceGroup?: string }): angular.IPromise<void> {
        this.isSaving = true;
        const item: any = group;
        const isCreating: boolean = item.isCreating;
        const parent = item.parent;
        const children = item.children;
        item.children = undefined;
        item.parent = undefined;
        const clone: Group = Object.assign({}, group);
        item.parent = parent;
        item.children = children;
        let p1: angular.IPromise<any>;
        let message: string;
        if (isCreating) {
            item.isCreating = false;
            p1 = this._storeService.dispatchers().groups.create(clone);
            message = 'adde';
        } else {
            p1 = this._storeService.dispatchers().groups.update(clone);
            message = 'update';
        }
        const promises: angular.IPromise<any>[] = [p1];
        if (
            clone.configMode === GroupConfigMode.ByType &&
            group.referenceGroup !== this._validationPageMapping[clone.type]
        ) {
            const widgets = this.widgetsDictionary[group.type] || [];
            //29438 : clear reference group if any for validation and manual input widgets
            const toClean = widgets.filter(w => (w.type === 'ManualInputs' || w.type === 'ValidatedValueSelection') && (w.configuration as any).referenceGroup);
            if (toClean.length > 0) {
                toClean.forEach(w => delete (w.configuration['referenceGroup']));
            }
            promises.push(this._storeService.dispatchers().validationPageConfigurations.updateReferenceGroup(clone.type, group.referenceGroup, widgets));
        }
        return this._$q
            .all(promises)
            .then(() => {
                this._loggerService.logSuccess(`Group successfully ${message}d.`);
                this.formController.$setPristine();
            })
            .catch(() => this._loggerService.logError(`Failed to ${message} group.`))
            .finally(() => this.isSaving = false);
    }

    private checkBindings(group: Group, oldRef: string, newRef: string): angular.IPromise<void> {
        const widgets = this.widgetsDictionary[group.type] || [];
        if (!oldRef) {
            const matchingTypes = this.groups.filter(d => d.type === group.type);
            if (matchingTypes.length) {
                oldRef = matchingTypes[0].guid;
            }
        }
        if (!oldRef) {
            oldRef = newRef;
        }
        return this._bindingCheckerService
            .checkBindings(newRef, oldRef, widgets)
            .then(data => {
                const { outside, bindResult, seriesByWidget } = data;
                if (outside.list.length > 0) {
                    this.createModal(outside, bindResult, seriesByWidget);
                } else {
                    this._bindingCheckerService.remapBoundSeries(seriesByWidget, bindResult);
                    this.actualSave(Object.assign(group, { referenceGroupGuid: newRef }));
                }
            })
            .catch(reason => {
                this._loggerService.logError('Unable to rebind series to new reference group.');
                console.log(reason);
            });
    }

    private createModal(
        outside: { reference: Group; list: IValueViewModel[] },
        bindResult: IBindingResult,
        seriesByWidget: { [index: string]: IBindable[] }
    ) {
        const widgets = this.widgetsDictionary[this.selectedItem.type] || [];
        this.modalConfig = this._bindingCheckerService.createModal({ outside, bindResult, seriesByWidget }, widgets);

        $('#modalSeriesMapConfirm').modal('show');
    }
}

export const wtModernModelContextual: ComponentDefinition = {
    componentId: 'wtModernModelContextual',
    controller: ModernModelContextualController,
    template: require('./modernModelContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        appConfig: '<',
        formController: '=',
        groups: '<',
        isSaving: '=?',
        onCancel: '&?',
        onSave: '&?',
        referenceGroupDictionary: '<',
        selectedItem: '<',
        selectedMiniature: '=?',
        templates: '<',
    }
};